import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { FuseConfigService } from '@fuse/services/config.service';
import { fuseAnimations } from '@fuse/animations';
import { LoginService } from './login.service';
import { AuthService } from '../auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { ACCOUNT_ROLE } from '../../account/account.model';
import { TranslateService } from '@ngx-translate/core';
import * as _  from 'lodash';

interface LoginLanguage {
    id: string;
    title: string;
    flag: string;
}

const LANGUAGE_STORAGE_KEY = 'selected_language';
const HOMEPAGE_URL = 'https://viecnhamay.vn/';

const LANGUAGES: LoginLanguage[] = [
    {
        id: 'en',
        title: 'English',
        flag: 'us',
    },
    {
        id: 'vi',
        title: 'Tiếng Việt',
        flag: 'vi',
    },
];

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class LoginComponent implements OnInit {
    languages: LoginLanguage[] = LANGUAGES;
    selectedLanguage: LoginLanguage;
    loginForm!: FormGroup;
    showError: boolean;
    version: string;
    loadingIndicator = false;
    private returnUrl = '';

    constructor(
        private _translateService: TranslateService,
        private _fuseConfigService: FuseConfigService,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private _fuseNavigationService: FuseNavigationService
    ) {
        this.showError = false;
        this.selectedLanguage = LANGUAGES[0];
        // Configure the layout
        this._fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true,
                },
                toolbar: {
                    hidden: true,
                },
                footer: {
                    hidden: true,
                },
                sidepanel: {
                    hidden: true,
                },
            },
        };
        this.version = environment.version;
    }

    ngOnInit(): void {
        this.returnUrl = this.getSafeReturnUrl(
            this.route.snapshot.queryParamMap.get('returnUrl') || ''
        );

        this.loginForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            remember: [false],
        });

        this.loadAccount();

        this.initializeLanguage();
    }

    setLanguage(lang: LoginLanguage): void {
        this.selectedLanguage = lang;
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.id);
        this._translateService.use(lang.id);
        location.reload();
    }

    onSubmit(data: any): void {
        this.loadingIndicator = true;
        this._loginService
            .login(data.email, data.password)
            .then((response) => {
                this.authService.storeUser(response);
                if (response.isAdmin) {
                    this._fuseNavigationService.setCurrentNavigation(
                        'adminNav'
                    );
                } else if (response.role && response.role === ACCOUNT_ROLE.SUPER_ADMIN) {
                    this._fuseNavigationService.setCurrentNavigation(
                        'superAdminNav'
                    );
                } else if (response.role && response.role === ACCOUNT_ROLE.HR) {
                    this._fuseNavigationService.setCurrentNavigation(
                        'hrNav'
                    );
                } else {
                    this._fuseNavigationService.setCurrentNavigation(
                        'staffNav'
                    );
                }
                const { role = '' } = response;
                const redirectUrl =
                    this.returnUrl ||
                    this.getSafeReturnUrl(this.authService.redirectUrl);
                this.authService.redirectUrl = '';
                if (redirectUrl) {
                    this.router.navigateByUrl(redirectUrl);
                } else if (role === ACCOUNT_ROLE.BCT_ADMIN) {
                    this.router.navigate(['/report']);
                } else {
                    this.router.navigate(['/dashboards']);
                }
                this.checkAndSaveAccount();
                this.loadingIndicator = false;
            })
            .catch((err) => {
                console.log(err);
                this.showError = true;
                this.loadingIndicator = false;
            });
    }

    loadAccount = () => {
        const email = localStorage.getItem('email');
        if (email && email.length) {
            const password = localStorage.getItem('password');
            this.loginForm.get('email')!.setValue(email);
            this.loginForm.get('password')!.setValue(password);
            this.loginForm.get('remember')!.setValue(true);
        }
    };

    checkAndSaveAccount = () => {
        if (this.loginForm.get('remember')!.value) {
            localStorage.setItem('email', this.loginForm.get('email')!.value);
            localStorage.setItem(
                'password',
                this.loginForm.get('password')!.value
            );
        } else {
            localStorage.removeItem('email');
            localStorage.removeItem('password');
        }
    };

    goToHomePage() {
        location.assign(HOMEPAGE_URL);
    }

    private getSafeReturnUrl(returnUrl: string): string {
        if (!returnUrl) {
            return '';
        }

        // Only allow in-app relative paths to avoid external redirects.
        if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
            return '';
        }

        return returnUrl;
    }

    private initializeLanguage(): void {
        const storedLanguageId = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (storedLanguageId) {
            this._translateService.use(storedLanguageId);
        }

        const activeLanguageId =
            storedLanguageId || this._translateService.currentLang;

        this.selectedLanguage =
            _.find(this.languages, { id: activeLanguageId }) ||
            this.languages[0];
    }
}
